import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../axiosConfig';
import { Form, Button, Card, Row, Col, Alert, Badge, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import estadosMunicipios from '../../components/estadosMunicipios.json';
import {
  cuadreGenero,
  cuadreEdades,
  distribucionInicial,
  validarFormulario,
  construirPayload,
  formularioInicial,
  montoEnUsd,
  formatoNumero,
} from './beneficiariosUtils';
import './Beneficiarios.css';

// Formulario único de "Beneficiarios sin datos": todo el caso se registra en
// un solo renglón (beneficiarios, ubicación, formación, patrocinio y pago),
// sin saltar a otras pantallas. Lo usan CreatePromocion y EditPromocion.

const Estado = ({ cuadre }) => {
  const color = cuadre.pendiente ? 'text-muted' : cuadre.ok ? 'text-success' : 'text-danger';
  const icono = cuadre.pendiente ? 'bi-info-circle' : cuadre.ok ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill';

  return (
    <div className={`mt-2 small ${cuadre.pendiente ? '' : 'fw-semibold'} ${color}`}>
      <i className={`bi ${icono} me-1`}></i>
      {cuadre.mensaje}
    </div>
  );
};

// Pregunta de Sí/No, igual que en la planilla de papel. Con dos opciones a la
// vista se ve de un vistazo qué se respondió; con un interruptor, "apagado" se
// confunde con "todavía no lo respondí".
const SiNo = ({ etiqueta, nombre, valor, onChange }) => (
  <div className="d-flex align-items-center flex-wrap gap-3">
    <Form.Label className="fw-semibold mb-0">{etiqueta}</Form.Label>
    <div className="d-flex gap-3">
      <Form.Check type="radio" id={`${nombre}_si`} name={nombre} label="Sí"
        checked={valor === true} onChange={() => onChange(nombre, true)} />
      <Form.Check type="radio" id={`${nombre}_no`} name={nombre} label="No"
        checked={valor === false} onChange={() => onChange(nombre, false)} />
    </div>
  </div>
);

const BeneficiarioForm = ({ titulo, registro, onGuardar, enviando, erroresServidor }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [opciones, setOpciones] = useState({
    centro: [], periodo: [], cohorte: [], procedencia: [], grupo_prioritario: [],
    grupo: [], cursos: [], patrocinante: [], forma_pago: [], grupo_etario: [],
  });
  const [form, setForm] = useState(() => formularioInicial(registro));
  const [distribucion, setDistribucion] = useState([]);
  // Al editar se conserva la tasa con la que se registró el pago: con la de
  // hoy cambiaría el equivalente en dólares de un pago antiguo.
  const [tasa, setTasa] = useState(registro?.tasa_bcv || null);
  const [costoCurso, setCostoCurso] = useState(null);
  const [errores, setErrores] = useState([]);

  useEffect(() => {
    const cargar = async () => {
      try {
        const { data } = await axios.get('/filter-promocion', { headers });
        setOpciones(data);
        setDistribucion(distribucionInicial(data.grupo_etario, registro?.distribucion_edades));
      } catch (error) {
        console.error('Error cargando opciones:', error);
        setErrores(['No se pudieron cargar las opciones del formulario. Recargue la página.']);
      }

      if (!registro?.tasa_bcv) {
        try {
          const { data } = await axios.get('/tasa_bcv', { headers });
          setTasa(data);
        } catch (error) {
          // Sin tasa se puede registrar igual; solo no se calcula el equivalente.
          console.error('Error cargando la tasa BCV:', error);
        }
      }
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Costo de referencia del curso elegido (si existe), para no tener que ir a
  // la pantalla de Costo Curso a consultarlo.
  useEffect(() => {
    if (!form.curso_id) {
      setCostoCurso(null);
      return;
    }
    axios.get('/costo-curso', { headers, params: { curso_id: form.curso_id } })
      .then(({ data }) => {
        const filas = Array.isArray(data) ? data : data?.data || [];
        const exacto = filas.find((c) => c.cohorte === form.cohorte && String(c.centro_id) === String(form.centro_id));
        setCostoCurso(exacto || filas[0] || null);
      })
      .catch(() => setCostoCurso(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.curso_id, form.cohorte, form.centro_id]);

  // Los errores del servidor (422) se suman a la alerta.
  useEffect(() => {
    if (erroresServidor && typeof erroresServidor === 'object') {
      setErrores(Object.values(erroresServidor).flat());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [erroresServidor]);

  const cambiar = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Las preguntas de Sí/No del formulario (aporte y patrocinio).
  const cambiarSiNo = (nombre, valor) => {
    setForm((prev) => ({ ...prev, [nombre]: valor }));
  };

  const cambiarEstado = (e) => {
    setForm((prev) => ({ ...prev, estado_id: e.target.value, municipio: '' }));
  };

  const cambiarEdad = (index, valor) => {
    setDistribucion((prev) => prev.map((fila, i) => (i === index ? { ...fila, cantidad: valor } : fila)));
  };

  const municipios = useMemo(() => {
    const estado = estadosMunicipios.find((e) => e.id_estado === parseInt(form.estado_id, 10));
    return estado ? estado.municipios : [];
  }, [form.estado_id]);

  const genero = cuadreGenero(form);
  const edades = cuadreEdades(form, distribucion);

  const opcionesCurso = opciones.cursos.map((c) => ({
    value: c.id,
    label: c.cod ? `${c.cod} — ${c.descripcion}` : c.descripcion,
  }));
  const opcionesPatrocinante = opciones.patrocinante.map((p) => ({
    value: p.id,
    label: p.rif_cedula ? `${p.nombre_patrocinante} (${p.rif_cedula})` : p.nombre_patrocinante,
  }));
  const valorSelect = (lista, id) => lista.find((o) => String(o.value) === String(id)) || null;

  const equivalente = montoEnUsd(form.monto, form.tipo_moneda, tasa?.tasa);

  const guardar = (e) => {
    e.preventDefault();
    const problemas = validarFormulario(form, distribucion);

    if (problemas.length > 0) {
      setErrores(problemas);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setErrores([]);
    onGuardar(construirPayload({ ...form, tasa_bcv_id: tasa?.id || '' }, distribucion));
  };

  return (
    <div className="row beneficiarios-modulo" style={{ marginTop: '30px' }}>
      <div className="col-lg-10 mx-auto">
        <div className="card-box" style={{ padding: '20px' }}>
          <h2 className="mb-1">{titulo}</h2>
          <p className="text-muted mb-3">
            Para casos atendidos de los que no se pueden recabar datos individuales. Se registra
            cuántas personas son y cómo se reparten por género y edad.
          </p>

          {errores.length > 0 && (
            <Alert variant="danger">
              <strong>No se puede guardar todavía:</strong>
              <ul className="mb-0 mt-1">
                {errores.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </Alert>
          )}

          <Form onSubmit={guardar} noValidate>
            {/* Orden de la planilla en papel (ajuste pedido el 23/09/2026):
                cohorte, período, centro, fecha, programa, grupo prioritario,
                procedencia, estado y municipio. */}
            <Card className="mb-3">
              <Card.Header><strong>1. Datos del caso</strong></Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="cohorte">
                      <Form.Label>Cohorte *</Form.Label>
                      <Form.Select name="cohorte" value={form.cohorte} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.cohorte.map((o) => <option key={o.id} value={o.descripcion}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="periodo_id">
                      <Form.Label>Período *</Form.Label>
                      <Form.Select name="periodo_id" value={form.periodo_id} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.periodo.map((o) => <option key={o.id} value={o.id}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="centro_id">
                      <Form.Label>Centro *</Form.Label>
                      <Form.Select name="centro_id" value={form.centro_id} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.centro.map((o) => <option key={o.id} value={o.id}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="fecha_promocion">
                      <Form.Label>Fecha de registro *</Form.Label>
                      <Form.Control type="date" name="fecha_promocion" value={form.fecha_promocion} onChange={cambiar} />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3" controlId="curso_id">
                      <Form.Label>Programa</Form.Label>
                      <Select
                        inputId="curso_id"
                        options={opcionesCurso}
                        value={valorSelect(opcionesCurso, form.curso_id)}
                        onChange={(op) => setForm((prev) => ({ ...prev, curso_id: op ? op.value : '' }))}
                        isClearable
                        placeholder="Buscar por código o nombre…"
                        noOptionsMessage={() => 'Sin resultados'}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="grupo">
                      <Form.Label>Grupo</Form.Label>
                      <Form.Select name="grupo" value={form.grupo} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.grupo.map((o) => <option key={o.id} value={o.descripcion}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {costoCurso && (
                  <div className="small text-muted mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Costo de referencia de este programa (cohorte {costoCurso.cohorte || '—'}):
                    inscripción {formatoNumero(costoCurso.costo_insc, 2)}, total {formatoNumero(costoCurso.total, 2)}.
                  </div>
                )}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="grupo_prioritario_id">
                      <Form.Label>Grupo prioritario</Form.Label>
                      <Form.Select name="grupo_prioritario_id" value={form.grupo_prioritario_id} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.grupo_prioritario.map((o) => <option key={o.id} value={o.id}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="procedencia_id">
                      <Form.Label>Procedencia *</Form.Label>
                      <Form.Select name="procedencia_id" value={form.procedencia_id} onChange={cambiar}>
                        <option value="">Seleccione…</option>
                        {opciones.procedencia.map((o) => <option key={o.id} value={o.id}>{o.descripcion}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="estado_id">
                      <Form.Label>Estado *</Form.Label>
                      <Form.Select name="estado_id" value={form.estado_id} onChange={cambiarEstado}>
                        <option value="">Seleccione…</option>
                        {estadosMunicipios.map((e) => <option key={e.id_estado} value={e.id_estado}>{e.estado}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="municipio">
                      <Form.Label>Municipio</Form.Label>
                      <Form.Select name="municipio" value={form.municipio} onChange={cambiar} disabled={!municipios.length}>
                        <option value="">{municipios.length ? 'Seleccione…' : 'Elija primero un estado'}</option>
                        {municipios.map((m) => <option key={m.municipio} value={m.municipio}>{m.municipio}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* ---------------------------------------------------------- */}
            <Card className="mb-3">
              <Card.Header><strong>2. Beneficiarios</strong></Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group controlId="cantidad_total">
                      <Form.Label>Total de beneficiarios *</Form.Label>
                      <Form.Control type="number" min="1" name="cantidad_total"
                        value={form.cantidad_total} onChange={cambiar} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="cantidad_masculino">
                      <Form.Label>Masculino *</Form.Label>
                      <Form.Control type="number" min="0" name="cantidad_masculino"
                        value={form.cantidad_masculino} onChange={cambiar} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="cantidad_femenino">
                      <Form.Label>Femenino *</Form.Label>
                      <Form.Control type="number" min="0" name="cantidad_femenino"
                        value={form.cantidad_femenino} onChange={cambiar} />
                    </Form.Group>
                  </Col>
                </Row>
                <Estado cuadre={genero} />

                <hr />
                <Form.Label className="fw-semibold">Distribución por grupo de edad *</Form.Label>
                <Row>
                  {distribucion.length === 0 && (
                    <Col><div className="text-muted small">Cargando los grupos de edad…</div></Col>
                  )}
                  {distribucion.map((fila, i) => (
                    <Col md={4} sm={6} key={fila.grupo_etario_id} className="mb-2">
                      <Form.Group controlId={`edad_${fila.grupo_etario_id}`}>
                        <Form.Label className="small mb-1">{fila.descripcion}</Form.Label>
                        <Form.Control type="number" min="0" value={fila.cantidad} placeholder="0"
                          onChange={(e) => cambiarEdad(i, e.target.value)} />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                <Estado cuadre={edades} />
              </Card.Body>
            </Card>

            {/* ---------------------------------------------------------- */}
            <Card className="mb-3">
              <Card.Header><strong>3. Aporte y patrocinio</strong></Card.Header>
              <Card.Body>
                <SiNo etiqueta="¿Realiza aporte?" nombre="realiza_aporte"
                  valor={form.realiza_aporte} onChange={cambiarSiNo} />

                {form.realiza_aporte && (
                  <>
                    <Row className="mt-3">
                      <Col md={4}>
                        <Form.Group className="mb-3" controlId="monto">
                          <Form.Label>Monto *</Form.Label>
                          <Form.Control type="number" min="0" step="0.01" name="monto" value={form.monto} onChange={cambiar} />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3" controlId="tipo_moneda">
                          <Form.Label>Moneda *</Form.Label>
                          <Form.Select name="tipo_moneda" value={form.tipo_moneda} onChange={cambiar}>
                            <option value="$">$</option>
                            <option value="bsF">bsF</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="forma_pago_id">
                          <Form.Label>Forma de pago</Form.Label>
                          <Form.Select name="forma_pago_id" value={form.forma_pago_id} onChange={cambiar}>
                            <option value="">Seleccione…</option>
                            {opciones.forma_pago.map((o) => <option key={o.id} value={o.id}>{o.descripcion}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="fecha_pago">
                          <Form.Label>Fecha de pago</Form.Label>
                          <Form.Control type="date" name="fecha_pago" value={form.fecha_pago} onChange={cambiar} />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group controlId="numero_comprobante">
                          <Form.Label>N.º de comprobante</Form.Label>
                          <Form.Control name="numero_comprobante" value={form.numero_comprobante} onChange={cambiar} maxLength={100} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="small text-muted mt-2">
                      {tasa
                        ? <>Tasa BCV del {String(tasa.created_at || '').slice(0, 10)}: <strong>{formatoNumero(tasa.tasa, 2)} Bs/$</strong>.</>
                        : 'No hay tasa BCV registrada: el monto se guarda, pero sin equivalente en dólares.'}
                      {equivalente != null && form.tipo_moneda === 'bsF' && (
                        <> Equivale a <strong>$ {formatoNumero(equivalente, 2)}</strong>.</>
                      )}
                    </div>
                  </>
                )}

                <hr />

                <SiNo etiqueta="¿Es patrocinado?" nombre="es_patrocinado"
                  valor={form.es_patrocinado} onChange={cambiarSiNo} />

                {form.es_patrocinado && (
                  <Row className="mt-3">
                    <Col md={8}>
                      <Form.Group controlId="patrocinante_id">
                        <Form.Label>Patrocinante *</Form.Label>
                        <Select
                          inputId="patrocinante_id"
                          options={opcionesPatrocinante}
                          value={valorSelect(opcionesPatrocinante, form.patrocinante_id)}
                          onChange={(op) => setForm((prev) => ({ ...prev, patrocinante_id: op ? op.value : '' }))}
                          isClearable
                          placeholder="Buscar…"
                          noOptionsMessage={() => 'Sin resultados'}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Va al final de la planilla y ocupa poco: es un campo de apoyo. */}
            <Card className="mb-3">
              <Card.Header><strong>4. Comentario / Observación</strong> <span className="text-muted small">(opcional)</span></Card.Header>
              <Card.Body>
                <Form.Group controlId="comentarios">
                  <Form.Control as="textarea" rows={2} size="sm" name="comentarios"
                    value={form.comentarios} onChange={cambiar}
                    placeholder="Anote aquí cualquier detalle del caso que no encaje en los campos anteriores." />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Resumen antes de guardar */}
            {distribucion.length > 0 && (
              <Table size="sm" bordered className="mb-3">
                <tbody>
                  <tr>
                    <th style={{ width: '40%' }}>Resumen</th>
                    <td>
                      <Badge bg="secondary" className="me-2">Total: {formatoNumero(form.cantidad_total)}</Badge>
                      <Badge bg={genero.ok ? 'success' : 'danger'} className="me-2">Género {genero.ok ? 'cuadra' : 'no cuadra'}</Badge>
                      <Badge bg={edades.ok ? 'success' : 'danger'}>Edades {edades.ok ? 'cuadran' : 'no cuadran'}</Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => navigate('/promocion')} disabled={enviando}>Cancelar</Button>
              <Button variant="success" type="submit" disabled={enviando}>
                {enviando ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default BeneficiarioForm;
